import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { CigBalances } from '@/models/cig';
import { environment } from 'src/environments/environment';
import { isLocalDevMimicEnabled } from '@/utils/local-mimic';
import { MarketplaceFilters, Period, SubgraphSortDirections, SubgraphSorts } from '@/models/pipes';
import { Address } from 'viem';
import { PlatformService } from './platform.service';
import { DelegationInfo } from './delegate-xyz.service';

@Injectable({
  providedIn: 'root'
})

export class StateService {
  private readonly MOCKED_LEGACY_WITHDRAWAL_ETH = 0.055;
  
  private menuOpen = new BehaviorSubject<boolean>(false);
  menuOpen$ = this.menuOpen.asObservable();

  private sideMenuActiveTab = new BehaviorSubject<'wrapping-tools' | 'my-bids'>('my-bids');
  sideMenuActiveTab$ = this.sideMenuActiveTab.asObservable();
  private stashWithdrawalRequested = new Subject<void>();
  stashWithdrawalRequested$ = this.stashWithdrawalRequested.asObservable();

  private outgoingBidsRefreshRequested = new Subject<void>();
  outgoingBidsRefreshRequested$ = this.outgoingBidsRefreshRequested.asObservable();

  private web3Connected = new BehaviorSubject<boolean>(false);

  // Reconnection lifecycle (true while auto-reconnecting)
  private reconnecting = new BehaviorSubject<boolean>(false);
  reconnecting$ = this.reconnecting.asObservable();

  // True once the application has checked auto-reconnection (connected or not)
  private checkedReconnection = new BehaviorSubject<boolean>(false);
  checkedReconnection$ = this.checkedReconnection.asObservable();

  private walletAddress = new BehaviorSubject<any>(null);

  // Wallet mimic: when set, walletAddress$ emits the mimic address instead of the real one
  private mimicAddress = new BehaviorSubject<string | null>(null);
  mimicAddress$ = this.mimicAddress.asObservable();

  /** Includes localhost wallet mimic (MCP / demos without a real wallet). */
  web3Connected$ = combineLatest([this.web3Connected, this.mimicAddress]).pipe(
    map(([connected, mimic]) => connected || (isLocalDevMimicEnabled() && mimic != null)),
    distinctUntilChanged(),
  );

  isMimicking$ = this.mimicAddress.pipe(map(addr => addr !== null));

  walletAddress$: Observable<string | null> = combineLatest([
    this.walletAddress,
    this.mimicAddress,
  ]).pipe(
    map(([real, mimic]) => mimic ?? real),
  );

  realWalletAddress$ = this.walletAddress.asObservable();

  private hasWithdrawal = new BehaviorSubject<number>(
    environment.mocked ? this.MOCKED_LEGACY_WITHDRAWAL_ETH : 0
  );
  hasWithdrawal$ = this.hasWithdrawal.asObservable();

  private cigBalances = new BehaviorSubject<CigBalances | null>(null);
  cigBalances$ = this.cigBalances.asObservable();

  private cigCEO = new BehaviorSubject<number | null>(null);
  cigCEO$ = this.cigCEO.asObservable();

  // Delegations
  private outgoingDelegations = new BehaviorSubject<DelegationInfo[]>([]);
  outgoingDelegations$ = this.outgoingDelegations.asObservable();

  private incomingDelegations = new BehaviorSubject<DelegationInfo[]>([]);
  incomingDelegations$ = this.incomingDelegations.asObservable();

  // JWT Token management
  private jwtToken = new BehaviorSubject<string | null>(null);
  jwtToken$ = this.jwtToken.asObservable();

  private jwtTokenExpiry = new BehaviorSubject<number | null>(null);
  jwtTokenExpiry$ = this.jwtTokenExpiry.asObservable();

  // Identity represented by the JWT token ("on behalf of" address)
  private authenticatedAsAddress = new BehaviorSubject<string | null>(null);
  authenticatedAsAddress$: Observable<string | null> = combineLatest([
    this.authenticatedAsAddress,
    this.mimicAddress,
  ]).pipe(
    map(([authenticatedAs, mimic]) => mimic ?? authenticatedAs),
  );
  private pagination = new BehaviorSubject<number>(1);
  pagination$: Observable<number> = this.pagination.asObservable();

  private marketState = new BehaviorSubject<string>('');
  marketState$ = this.marketState.asObservable();

  private marketLoading = new BehaviorSubject<boolean>(false);
  marketLoading$ = this.marketLoading.asObservable();

  private loadingMore = new BehaviorSubject<boolean>(false);
  loadingMore$ = this.loadingMore.asObservable();

  private documentClick = new Subject<MouseEvent>();
  documentClick$ = this.documentClick.asObservable();

  private keyDownEscape = new Subject<KeyboardEvent>();
  keyDownEscape$ = this.keyDownEscape.asObservable();

  private isMobile = new BehaviorSubject<boolean>(false);
  isMobile$ = this.isMobile.asObservable();

  private hideFlashLoans = new BehaviorSubject<boolean>(true);
  hideFlashLoans$ = this.hideFlashLoans.asObservable();

  private hideMarketplaceIcons = new BehaviorSubject<boolean>(false);
  hideMarketplaceIcons$ = this.hideMarketplaceIcons.asObservable();

  marketplaceFilters: MarketplaceFilters[] = ['All', 'Native', 'CigStrategy', 'PunkStrategy', /*'PunkAuction', */'Opensea', 'Blur', 'NFTX', 'Gondi'];
  activeMarketplaceFilterModel: MarketplaceFilters = this.marketplaceFilters[0];
  
  private activeMarketplaceFilters = new BehaviorSubject(this.marketplaceFilters[0]);
  activeMarketplaceFilters$ = this.activeMarketplaceFilters.asObservable();

  // Aggregated floor (all marketplaces), stored in ETH
  private aggregatedFloor = new BehaviorSubject<number>(0);
  aggregatedFloor$ = this.aggregatedFloor.asObservable();

  periods: Period[] = [
    { key: '24h', value: '24 Hours', limit: 1 },
    { key: '7d', value: '7 Days', limit: 7 },
    { key: '30d', value: '30 Days', limit: 30 },
    { key: '6m', value: '6 Months', limit: 180 },
    { key: '1y', value: '1 Year', limit: 365 },
    { key: 'all', value: 'All Time', limit: 3650 }
  ];

  // Top Sales
  private _defaultTopSalesPeriod: any = this.periods[1].key || '7d';
  private activeTopSalesPeriod = new BehaviorSubject<Period>(this.periods[1]);
  activeTopSalesPeriod$ = this.activeTopSalesPeriod.asObservable() as Observable<Period>;

  get defaultTopSalesPeriod(): any {
    return this._defaultTopSalesPeriod;
  }

  set defaultTopSalesPeriod(value: any) {
    this._defaultTopSalesPeriod = value;
    const period = this.periods.find(p => p.key === value);
    if (period) {
      this.activeTopSalesPeriod.next(period);
    }
  }

  // Visual Data
  private _defaultPeriod: any = this.periods[4].key || '1y';
  private activePeriod = new BehaviorSubject<any>(this.periods[4]);

  activePeriod$ = this.activePeriod.asObservable() as Observable<Period>;

  get defaultPeriod(): any {
    return this._defaultPeriod;
  }

  set defaultPeriod(value: any) {
    this._defaultPeriod = value;
    const period = this.periods.find(p => p.key === value);
    if (period) {
      this.activePeriod.next(period);
    }
  }

  private salesLoaded = new BehaviorSubject<number>(0);
  salesLoaded$ = this.salesLoaded.asObservable();

  currentSort:{sort:SubgraphSorts, direction: SubgraphSortDirections} = {
    sort: 'blockTimestamp',
    direction: 'desc'
  };

  sortParams: any = {};

  punkBoxAddress!: string | null;
  
  constructor(private platformService: PlatformService) {
    // Initialize isMobile only on client side
    if (this.platformService.isClient) {
      this.isMobile.next(this.platformService.innerWidth < 801);
      
      // Load JWT token from localStorage on initialization
      this.loadJwtTokenFromStorage();
      this.loadMimicFromSessionStorage();
    }
    if (environment.collection !== 'cryptopunks') {
      this.marketplaceFilters = this.marketplaceFilters
        .filter((filter: any) => filter !== 'PunkStrategy')
        .filter((filter: any) => filter !== 'PunkAuction');
    }

    // CryptoPunks V1 does not have an NFTX pool; never expose NFTX in UI filters.
    if (environment.collection === 'cryptopunks-v1') {
      this.marketplaceFilters = this.marketplaceFilters.filter((filter: any) => filter !== 'NFTX');

      // Safety: if something pre-set the model to NFTX, reset to default.
      if (this.activeMarketplaceFilterModel === 'NFTX') {
        this.activeMarketplaceFilterModel = this.marketplaceFilters[0];
        this.activeMarketplaceFilters.next(this.marketplaceFilters[0]);
      }
    }
  }

  /**
   * Load JWT token from localStorage
   */
  private loadJwtTokenFromStorage(): void {
    if (this.platformService.isServer) {
      return;
    }

    try {
      const token = localStorage.getItem('jwt_token');
      const expiry = localStorage.getItem('jwt_token_expiry');
      const authAddress = localStorage.getItem('jwt_authenticated_as_address');
      
      if (token && expiry) {
        const expiryTime = parseInt(expiry, 10);
        // Check if token is still valid (not expired)
        if (expiryTime > Date.now()) {
          this.jwtToken.next(token);
          this.jwtTokenExpiry.next(expiryTime);
          this.authenticatedAsAddress.next(authAddress?.toLowerCase() || null);
        } else {
          // Token expired, clear it
          this.clearJwtToken();
        }
      }
    } catch (error) {
      console.error('[StateService] Error loading JWT token from storage:', error);
    }
  }

  setIsMobile(status: boolean): void {
    this.isMobile.next(status);
  }

  getIsMobile(): boolean {
    return this.isMobile.getValue();
  }

  setWalletAddress(address: string | null | undefined): void {
    address = address?.toLowerCase();
    this.walletAddress.next(address);
  }

  /**
   * Returns the effective wallet address: mimic address if active, otherwise the real one.
   */
  getWalletAddress(): string | null {
    return this.mimicAddress.getValue() ?? this.walletAddress.getValue();
  }

  getRealWalletAddress(): string | null {
    return this.walletAddress.getValue();
  }

  // Mimic methods

  setMimicAddress(address: string): void {
    const normalized = address.toLowerCase();
    this.mimicAddress.next(normalized);
    if (this.platformService.isClient) {
      try {
        sessionStorage.setItem('mimic_address', normalized);
      } catch (e) {
        console.error('[StateService] Error persisting mimic address:', e);
      }
    }
  }

  clearMimicAddress(): void {
    this.mimicAddress.next(null);
    if (this.platformService.isClient) {
      try {
        sessionStorage.removeItem('mimic_address');
      } catch (e) {
        console.error('[StateService] Error clearing mimic address:', e);
      }
    }
  }

  getMimicAddress(): string | null {
    return this.mimicAddress.getValue();
  }

  isMimicking(): boolean {
    return this.mimicAddress.getValue() !== null;
  }

  private loadMimicFromSessionStorage(): void {
    if (this.platformService.isServer) return;
    try {
      const stored = sessionStorage.getItem('mimic_address');
      if (stored) {
        this.mimicAddress.next(stored.toLowerCase());
      }
    } catch (e) {
      console.error('[StateService] Error loading mimic address from storage:', e);
    }
  }

  getAuthenticatedAsAddress(): string | null {
    return this.authenticatedAsAddress.getValue();
  }

  getEffectiveAuthenticatedAsAddress(): string | null {
    return this.mimicAddress.getValue() ?? this.authenticatedAsAddress.getValue();
  }

  setWeb3Connected(status: boolean): void {
    this.web3Connected.next(status);
  }

  getWeb3Connected(): boolean {
    const connected = this.web3Connected.getValue();
    return connected || (isLocalDevMimicEnabled() && this.isMimicking());
  }

  setReconnecting(status: boolean): void {
    this.reconnecting.next(status);
  }

  getReconnecting(): boolean {
    return this.reconnecting.getValue();
  }

  setCheckedReconnection(status: boolean): void {
    this.checkedReconnection.next(status);
  }

  getCheckedReconnection(): boolean {
    return this.checkedReconnection.getValue();
  }

  setHasWithdrawal(status: number): void {
    if (environment.mocked) {
      this.hasWithdrawal.next(this.MOCKED_LEGACY_WITHDRAWAL_ETH);
      return;
    }
    this.hasWithdrawal.next(status);
  }

  getHasWithdrawal(): number {
    return this.hasWithdrawal.getValue();
  }

  setPagination(page: number): void {
    this.pagination.next(page);
  }

  setMarketState(state: any): void {
    this.marketState.next(state);
  }

  setActivePeriod(period: any): void {
    this.activePeriod.next(period);
    this._defaultPeriod = period.key;
  }

  getActivePeriod(): any {
    return this.activePeriod.getValue();
  }

  setCigBalances(balances: CigBalances | null): void {
    this.cigBalances.next({ ...this.cigBalances.getValue(), ...balances });
  }

  setCigCEO(ceo: number|null): void {
    this.cigCEO.next(ceo);
  }

  setDocumentClick(event: MouseEvent): void {
    this.documentClick.next(event);
  }

  setKeydownEscape(event: KeyboardEvent): void {
    this.keyDownEscape.next(event);
  }

  setHideFlashLoans(status: boolean): void {
    this.hideFlashLoans.next(status);
  }

  setHideMarketplaceIcons(status: boolean): void {
    this.hideMarketplaceIcons.next(status);
  }

  getHideMarketplaceIcons(): boolean {
    return this.hideMarketplaceIcons.getValue();
  }

  getHideFlashLoans(): boolean {
    return this.hideFlashLoans.getValue();
  }

  setActiveTopSalesPeriod(period: Period): void {
    this.activeTopSalesPeriod.next(period);
    this._defaultTopSalesPeriod = period.key;
  }

  getActiveTopSalesPeriod(): Period {
    return this.activeTopSalesPeriod.getValue();
  }

  setMenuOpen(status: boolean): void {
    this.menuOpen.next(status);
  }

  setMarketLoading(loading: boolean): void {
    this.marketLoading.next(loading)
  }

  getMenuOpen(): boolean {
    return this.menuOpen.getValue();
  }

  setSideMenuActiveTab(tab: 'wrapping-tools' | 'my-bids'): void {
    this.sideMenuActiveTab.next(tab);
  }

  getSideMenuActiveTab(): 'wrapping-tools' | 'my-bids' {
    return this.sideMenuActiveTab.getValue();
  }

  requestStashWithdrawal(): void {
    this.stashWithdrawalRequested.next();
  }

  requestOutgoingBidsRefresh(): void {
    this.outgoingBidsRefreshRequested.next();
  }

  loadMoreSales(page: number, limit: number): void {
    console.log('[StateService] loadMoreSales - updating UI state only');
    this.salesLoaded.next(page * limit);
    this.loadingMore.next(true);
  }

  setSalesLoaded(loaded: number): void {
    this.salesLoaded.next(loaded);
  }

  getSalesLoaded(): number {
    return this.salesLoaded.getValue();
  }

  setLoadingMore(loading:boolean) {
    this.loadingMore.next(loading)
  }

  setMarketplaceFilter($event: MarketplaceFilters): void {
    // CryptoPunks V1 does not support NFTX.
    if (environment.collection === 'cryptopunks-v1' && $event === 'NFTX') {
      this.activeMarketplaceFilterModel = this.marketplaceFilters[0];
      this.activeMarketplaceFilters.next(this.marketplaceFilters[0]);
      return;
    }
    this.activeMarketplaceFilters.next($event);
  }

  getMarketplaceFilter() {
    return this.activeMarketplaceFilters.getValue()
  }

  resetMarketplaceFilter(): void {
    this.activeMarketplaceFilters.next(this.marketplaceFilters[0]);
  }

  // Delegations methods
  setOutgoingDelegations(delegations: DelegationInfo[]): void {
    this.outgoingDelegations.next(delegations);
  }

  getOutgoingDelegations(): DelegationInfo[] {
    return this.outgoingDelegations.getValue();
  }

  setIncomingDelegations(delegations: DelegationInfo[]): void {
    this.incomingDelegations.next(delegations);
  }

  getIncomingDelegations(): DelegationInfo[] {
    return this.incomingDelegations.getValue();
  }

  getTotalDelegationsCount(): number {
    const outgoing = this.outgoingDelegations.getValue();
    const incoming = this.incomingDelegations.getValue();

    // Compter les adresses uniques
    const uniqueAddresses = new Set<string>();

    // Ajouter les adresses "to" des délégations sortantes
    outgoing.forEach(delegation => uniqueAddresses.add(delegation.to.toLowerCase()));

    // Ajouter les adresses "from" des délégations entrantes
    incoming.forEach(delegation => uniqueAddresses.add(delegation.from.toLowerCase()));

    return uniqueAddresses.size;
  }

  setAggregatedFloor(value: number): void {
    this.aggregatedFloor.next(value);
  }

  getAggregatedFloor(): number {
    return this.aggregatedFloor.getValue();
  }

  // JWT Token methods
  setJwtToken(token: string, expiresIn: number, authenticatedAsAddress?: string): void {
    if (this.platformService.isServer) {
      return;
    }

    // Calculate expiry timestamp (expiresIn is in seconds)
    const expiryTime = Date.now() + (expiresIn * 1000);
    
    this.jwtToken.next(token);
    this.jwtTokenExpiry.next(expiryTime);
    this.authenticatedAsAddress.next(authenticatedAsAddress?.toLowerCase() || null);

    // Store in localStorage
    try {
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('jwt_token_expiry', expiryTime.toString());
      if (authenticatedAsAddress) {
        localStorage.setItem('jwt_authenticated_as_address', authenticatedAsAddress.toLowerCase());
      } else {
        localStorage.removeItem('jwt_authenticated_as_address');
      }
    } catch (error) {
      console.error('[StateService] Error storing JWT token:', error);
    }
  }

  getJwtToken(): string | null {
    const token = this.jwtToken.getValue();
    const expiry = this.jwtTokenExpiry.getValue();

    // Check if token is still valid
    if (token && expiry && expiry > Date.now()) {
      return token;
    }

    // Token expired or doesn't exist, clear it
    if (token) {
      this.clearJwtToken();
    }

    return null;
  }

  clearJwtToken(): void {
    this.jwtToken.next(null);
    this.jwtTokenExpiry.next(null);
    this.authenticatedAsAddress.next(null);

    // Remove from localStorage
    if (this.platformService.isClient) {
      try {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_token_expiry');
        localStorage.removeItem('jwt_authenticated_as_address');
      } catch (error) {
        console.error('[StateService] Error clearing JWT token:', error);
      }
    }
  }

  getJwtTokenExpiry(): number | null {
    return this.jwtTokenExpiry.getValue();
  }

  isTokenValid(): boolean {
    const token = this.jwtToken.getValue();
    const expiry = this.jwtTokenExpiry.getValue();

    return !!(token && expiry && expiry > Date.now());
  }

}
